import {
    type ChangeEvent,
    type RefObject,
    createElement,
    useEffect,
    useState,
    useRef
} from "react";
import {
    Platform
} from "react-native";
import type {
    FileUploadPreviewReleaseConstantType,
    FileUploadOpenPickerConstantType,
    FileUploadPickerConstantType
} from "./type";
import {
    createIncomingFile,
    revokePreviewUri
} from "./util";

const FILE_UPLOAD_DROP_FILES_TYPE = "Files";

const FILE_UPLOAD_IMAGE_ACCEPT = "image/*";

const FILE_UPLOAD_CAMERA_CAPTURE = "environment";

export const useFilePicker = ({
    isDisabled,
    isMultiple,
    onFiles,
    accept
}: FileUploadPickerConstantType) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const list = Array.from(event.target.files ?? []);

        event.target.value = "";

        if(!list.length) {
            return;
        }

        onFiles(list.map(createIncomingFile));
    };

    const openPicker = ({
        source
    }: FileUploadOpenPickerConstantType = {}) => {
        const input = inputRef.current;

        if(!input || isDisabled) {
            return;
        }

        const isImageSource = source === "camera" || source === "library";

        input.accept = isImageSource ? FILE_UPLOAD_IMAGE_ACCEPT : (accept ?? []).join(",");

        if(source === "camera") {
            input.setAttribute("capture", FILE_UPLOAD_CAMERA_CAPTURE);
        } else {
            input.removeAttribute("capture");
        }

        input.click();
    };

    const renderInput = () => {
        if(Platform.OS !== "web") {
            return null;
        }

        return createElement("input", {
            accept: (accept ?? []).join(","),
            disabled: isDisabled,
            multiple: isMultiple,
            "aria-hidden": true,
            style: {
                display: "none"
            },
            ref: inputRef,
            tabIndex: -1,
            type: "file",
            onChange
        });
    };

    return {
        renderInput,
        openPicker
    };
};

export const useFileDrop = ({
    isEnabled,
    onFiles,
    nodeRef
}: {
    onFiles: FileUploadPickerConstantType["onFiles"];
    nodeRef: RefObject<unknown>;
    isEnabled: boolean;
}) => {
    const [
        dragCount,
        setDragCount
    ] = useState<number | null>(null);

    const onFilesRef = useRef(onFiles);

    onFilesRef.current = onFiles;

    useEffect(() => {
        if(Platform.OS !== "web" || !isEnabled || typeof HTMLElement === "undefined") {
            return;
        }

        const node = nodeRef.current;

        const doc = globalThis.document as Document | undefined;

        if(!(node instanceof HTMLElement) || !doc) {
            return;
        }

        let depth = 0;

        const getHasFiles = (event: DragEvent) => {
            return Array.from(event.dataTransfer?.types ?? []).includes(FILE_UPLOAD_DROP_FILES_TYPE);
        };

        const onDragEnter = (event: DragEvent) => {
            if(!getHasFiles(event)) {
                return;
            }

            event.preventDefault();

            depth += 1;

            setDragCount(event.dataTransfer?.items.length ?? 0);
        };

        const onDragOver = (event: DragEvent) => {
            if(!getHasFiles(event)) {
                return;
            }

            event.preventDefault();

            if(event.dataTransfer) {
                event.dataTransfer.dropEffect = "copy";
            }
        };

        const onDragLeave = (event: DragEvent) => {
            if(!getHasFiles(event)) {
                return;
            }

            depth = Math.max(depth - 1, 0);

            if(depth === 0) {
                setDragCount(null);
            }
        };

        const onDrop = (event: DragEvent) => {
            if(!getHasFiles(event)) {
                return;
            }

            event.preventDefault();

            depth = 0;

            setDragCount(null);

            const list = Array.from(event.dataTransfer?.files ?? []);

            if(list.length) {
                onFilesRef.current(list.map(createIncomingFile));
            }
        };

        const onPaste = (event: ClipboardEvent) => {
            const list = Array.from(event.clipboardData?.files ?? []);

            if(!list.length) {
                return;
            }

            event.preventDefault();

            onFilesRef.current(list.map(createIncomingFile));
        };

        const onDocumentDrag = (event: DragEvent) => {
            if(getHasFiles(event)) {
                event.preventDefault();
            }
        };

        node.addEventListener("dragenter", onDragEnter);

        node.addEventListener("dragover", onDragOver);

        node.addEventListener("dragleave", onDragLeave);

        node.addEventListener("drop", onDrop);

        node.addEventListener("paste", onPaste);

        doc.addEventListener("dragover", onDocumentDrag);

        doc.addEventListener("drop", onDocumentDrag);

        return () => {
            node.removeEventListener("dragenter", onDragEnter);

            node.removeEventListener("dragover", onDragOver);

            node.removeEventListener("dragleave", onDragLeave);

            node.removeEventListener("drop", onDrop);

            node.removeEventListener("paste", onPaste);

            doc.removeEventListener("dragover", onDocumentDrag);

            doc.removeEventListener("drop", onDocumentDrag);

            setDragCount(null);
        };
    }, [
        isEnabled,
        nodeRef
    ]);

    return {
        isDragOver: dragCount !== null,
        dragCount
    };
};

export const useFilePreviewRelease = ({
    removedEntries,
    files
}: FileUploadPreviewReleaseConstantType) => {
    const referencedRef = useRef<Set<string>>(new Set());

    const latestRef = useRef({
        removedEntries,
        files
    });

    useEffect(() => {
        const referenced = new Set<string>();

        [
            ...files,
            ...removedEntries.map((entry) => entry.file)
        ].forEach((file) => {
            if(file.previewUri) {
                referenced.add(file.previewUri);
            }
        });

        referencedRef.current.forEach((uri) => {
            if(!referenced.has(uri)) {
                revokePreviewUri({
                    uri
                });
            }
        });

        referencedRef.current = referenced;

        latestRef.current = {
            removedEntries,
            files
        };
    }, [
        removedEntries,
        files
    ]);

    useEffect(() => {
        return () => {
            const {
                removedEntries: latestEntries,
                files: latestFiles
            } = latestRef.current;

            const keptUris = new Set(latestFiles.map((file) => file.previewUri));

            latestEntries.forEach((entry) => {
                if(!keptUris.has(entry.file.previewUri)) {
                    revokePreviewUri({
                        uri: entry.file.previewUri
                    });
                }
            });
        };
    }, []);
};
